import { useEffect, useRef, useState } from "react";
import { FileText, ListTodo, StickyNote, ChevronRight } from "lucide-react";

// ---------------------------------------------------------------------------
// Types (spiegeln shared/searchTypes.ts der Note-App)
// ---------------------------------------------------------------------------
interface GlobalPage {
  id: number;
  title: string;
  icon: string | null;
  snippet: string;
  sectionName: string | null;
}
interface GlobalSubtask {
  subtaskId: string;
  pageId: number;
  pageTitle: string;
  rowTitle: string;
  snippet: string;
  matchText: string;
}
interface GlobalQuickNote {
  id: string;
  title: string | null;
  snippet: string;
}
interface NoteSearchResult {
  query: string;
  pages: GlobalPage[];
  subtasks: GlobalSubtask[];
  quickNotes: GlobalQuickNote[];
  totalHits: number;
}

// ---------------------------------------------------------------------------
// API-Aufruf gegen die Note-App (same-origin, notes_session Cookie greift)
// ---------------------------------------------------------------------------
async function searchNotes(q: string, signal: AbortSignal): Promise<NoteSearchResult | null> {
  try {
    const input = encodeURIComponent(JSON.stringify({ json: { q, limit: 12 } }));
    const res = await fetch(`/nexus/api/trpc/search.global?input=${input}`, {
      credentials: "include",
      signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    // tRPC v11 Batch-Format: [{ result: { data: { json: ... } } }]
    const result = data?.[0]?.result?.data?.json ?? data?.result?.data?.json ?? data;
    return result as NoteSearchResult;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Snippet-Rendering: parst <mark>…</mark> sicher in React-Elemente
// Statt dangerouslySetInnerHTML werden nur <mark>-Tags erkannt und als
// sichere React-Spans gerendert. Alle anderen HTML-Tags werden escaped.
// ---------------------------------------------------------------------------
function Snippet({ html }: { html: string }) {
  // Split on <mark> and </mark> tags, alternating between plain text and highlighted text
  const parts: Array<{ text: string; highlighted: boolean }> = [];
  const regex = /<mark>(.*?)<\/mark>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(html)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      parts.push({ text: html.slice(lastIndex, match.index), highlighted: false });
    }
    // The highlighted text (inner content of <mark>)
    parts.push({ text: match[1], highlighted: true });
    lastIndex = regex.lastIndex;
  }
  // Remaining text after last match
  if (lastIndex < html.length) {
    parts.push({ text: html.slice(lastIndex), highlighted: false });
  }

  // Strip any remaining HTML tags from text segments (safety measure)
  function stripTags(str: string): string {
    return str.replace(/<[^>]*>/g, "");
  }

  return (
    <span className="note-search-snippet">
      {parts.map((part, i) =>
        part.highlighted ? (
          <mark
            key={i}
            style={{
              background: "rgba(0, 163, 224, 0.25)",
              color: "inherit",
              borderRadius: 2,
              padding: "0 1px",
            }}
          >
            {stripTags(part.text)}
          </mark>
        ) : (
          <span key={i}>{stripTags(part.text)}</span>
        )
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Haupt-Komponente
// ---------------------------------------------------------------------------
interface Props {
  query: string;
  onClose: () => void;
}

export function NoteSearchDropdown({ query, onClose }: Props) {
  const [result, setResult] = useState<NoteSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Suche mit Debounce (300 ms)
  useEffect(() => {
    if (query.length < 2) {
      setResult(null);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      const res = await searchNotes(query, ctrl.signal);
      if (!ctrl.signal.aborted) {
        setResult(res);
        setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query]);

  // Schließen bei Klick außerhalb
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // Navigation zur Note-App-Seite via postMessage an den Notes-Iframe
  function navigateTo(pageId: number, highlight?: string) {
    const iframe = document.querySelector<HTMLIFrameElement>('iframe[src*="/nexus"]') ??
      document.querySelector<HTMLIFrameElement>('iframe[data-app="notes"]');
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "notes:navigate", pageId, highlight },
        window.location.origin
      );
    }
    onClose();
  }

  if (query.length < 2) return null;

  const hasResults = result && result.totalHits > 0;
  const isEmpty = result && result.totalHits === 0 && !loading;

  return (
    <div
      ref={dropdownRef}
      className="note-search-dropdown"
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        width: 380,
        maxHeight: 480,
        overflowY: "auto",
        background: "var(--surface-2, #1a1f2e)",
        border: "1px solid var(--border, rgba(255,255,255,0.1))",
        borderRadius: 10,
        boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
        zIndex: 9999,
        padding: "6px 0",
      }}
    >
      {loading && (
        <div style={{ padding: "12px 16px", color: "var(--text-3, rgba(255,255,255,0.4))", fontSize: 12 }}>
          Suche…
        </div>
      )}

      {isEmpty && (
        <div style={{ padding: "12px 16px", color: "var(--text-3, rgba(255,255,255,0.4))", fontSize: 12 }}>
          Keine Treffer für „{query}"
        </div>
      )}

      {hasResults && (
        <>
          {/* Seiten */}
          {result.pages.length > 0 && (
            <section>
              <div style={{
                padding: "6px 14px 4px",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--text-3, rgba(255,255,255,0.35))",
                fontFamily: "var(--font-shell-mono, monospace)",
              }}>
                Seiten
              </div>
              {result.pages.map((page) => (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => navigateTo(page.id, query)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    width: "100%",
                    padding: "8px 14px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <span style={{ marginTop: 1, color: "var(--accent, #00A3E0)", flexShrink: 0 }}>
                    <FileText size={13} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--text, #fff)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {page.title}
                    </span>
                    {page.snippet && (
                      <span style={{ display: "-webkit-box", fontSize: 11, color: "var(--text-3, rgba(255,255,255,0.4))", marginTop: 2, overflow: "hidden", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } as React.CSSProperties}>
                        <Snippet html={page.snippet} />
                      </span>
                    )}
                  </span>
                  <ChevronRight size={12} style={{ color: "var(--text-3, rgba(255,255,255,0.3))", flexShrink: 0, marginTop: 2 }} />
                </button>
              ))}
            </section>
          )}

          {/* Tasks */}
          {result.subtasks.length > 0 && (
            <section style={{ borderTop: result.pages.length > 0 ? "1px solid var(--border, rgba(255,255,255,0.07))" : undefined, marginTop: result.pages.length > 0 ? 4 : 0, paddingTop: result.pages.length > 0 ? 4 : 0 }}>
              <div style={{
                padding: "6px 14px 4px",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--text-3, rgba(255,255,255,0.35))",
                fontFamily: "var(--font-shell-mono, monospace)",
              }}>
                Tasks
              </div>
              {result.subtasks.map((st) => (
                <button
                  key={`${st.pageId}-${st.subtaskId}`}
                  type="button"
                  onClick={() => navigateTo(st.pageId, st.matchText)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    width: "100%",
                    padding: "8px 14px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <span style={{ marginTop: 1, color: "rgba(134,239,172,0.8)", flexShrink: 0 }}>
                    <ListTodo size={13} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--text, #fff)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {st.matchText}
                    </span>
                    <span style={{ display: "block", fontSize: 11, color: "var(--text-3, rgba(255,255,255,0.4))", marginTop: 2 }}>
                      {st.rowTitle} · {st.pageTitle}
                    </span>
                  </span>
                  <ChevronRight size={12} style={{ color: "var(--text-3, rgba(255,255,255,0.3))", flexShrink: 0, marginTop: 2 }} />
                </button>
              ))}
            </section>
          )}

          {/* Quick Notes */}
          {result.quickNotes.length > 0 && (
            <section style={{ borderTop: "1px solid var(--border, rgba(255,255,255,0.07))", marginTop: 4, paddingTop: 4 }}>
              <div style={{
                padding: "6px 14px 4px",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--text-3, rgba(255,255,255,0.35))",
                fontFamily: "var(--font-shell-mono, monospace)",
              }}>
                Quick Notes
              </div>
              {result.quickNotes.map((qn) => (
                <button
                  key={qn.id}
                  type="button"
                  onClick={() => {
                    // Quick Notes haben keine eigene Seiten-ID — navigiere zur Inbox
                    const iframe = document.querySelector<HTMLIFrameElement>('iframe[src*="/nexus"]') ??
                      document.querySelector<HTMLIFrameElement>('iframe[data-app="notes"]');
                    if (iframe?.contentWindow) {
                      iframe.contentWindow.postMessage(
                        { type: "notes:navigate-route", route: "/inbox" },
                        window.location.origin
                      );
                    }
                    onClose();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    width: "100%",
                    padding: "8px 14px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <span style={{ marginTop: 1, color: "rgba(253,224,71,0.8)", flexShrink: 0 }}>
                    <StickyNote size={13} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {qn.title && (
                      <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--text, #fff)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {qn.title}
                      </span>
                    )}
                    {qn.snippet && (
                      <span style={{ display: "block", fontSize: 11, color: "var(--text-3, rgba(255,255,255,0.4))", marginTop: 2 }}>
                        <Snippet html={qn.snippet} />
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
